// R45: Slim component dat leest uit gedeelde lineCache (zie orderLinesPrefetch).
// Wanneer een tegel ooit door IntersectionObserver in beeld is geweest, is de
// data al ingeladen → expand voelt instant aan (< 50ms render).
import { useEffect, useState } from "react";
import { Image as ImageIcon, ImageOff } from "lucide-react";
import {
  fetchOrderLines,
  getCachedLines,
  type Line,
} from "@/lib/orderLinesPrefetch";

interface Props {
  orderid: number;
  ordernumber?: string;
}

const OrderLinesExpansion = ({ orderid, ordernumber }: Props) => {
  const [lines, setLines] = useState<Line[] | null>(getCachedLines(orderid));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (lines) return;
    let cancelled = false;
    setLoading(true);
    fetchOrderLines(orderid, ordernumber)
      .then((d) => { if (!cancelled) { setLines(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.message ?? String(e)); setLoading(false); } });
    return () => { cancelled = true; };
  }, [orderid, ordernumber, lines]);

  if (loading && !lines) {
    return (
      <div className="rounded-md border bg-muted/30">
        <ul className="divide-y">
          {[0, 1, 2].map((i) => (
            <li key={i} className="grid grid-cols-[64px_1fr_auto] md:grid-cols-[56px_1fr_90px_70px] gap-3 px-3 py-3 items-center min-h-[64px]">
              <div className="h-16 w-16 md:h-12 md:w-12 shrink-0 rounded bg-muted animate-pulse" />
              <div className="min-w-0 space-y-2">
                <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                <div className="h-3 w-1/3 rounded bg-muted animate-pulse" />
              </div>
              <div className="hidden md:block h-3 w-16 rounded bg-muted animate-pulse" />
              <div className="hidden md:block h-3 w-8 ml-auto rounded bg-muted animate-pulse" />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (error) {
    return <div className="px-2 py-4 text-sm text-destructive">Kon productregels niet laden, probeer later opnieuw.</div>;
  }
  if (!lines || lines.length === 0) {
    return <div className="px-2 py-4 text-sm text-muted-foreground">Geen productregels gevonden</div>;
  }

  return (
    <div className="max-h-[420px] overflow-y-auto overscroll-contain rounded-md border bg-muted/30">
      <div className="hidden md:grid grid-cols-[56px_1fr_90px_70px] gap-3 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground border-b bg-muted/40">
        <div></div><div>Product</div><div>Eenheid</div><div className="text-right">Aantal</div>
      </div>
      <ul className="divide-y">
        {lines.map((l, idx) => {
          const showImg = !!l.image && !failedImageIds.has(l.id);
          const eager = idx < 5;
          return (
            <li
              key={l.id}
              className="grid grid-cols-[64px_1fr_auto] md:grid-cols-[56px_1fr_90px_70px] gap-3 px-3 py-3 items-center min-h-[64px] active:bg-accent/40 transition-colors"
            >
              <div className="h-16 w-16 md:h-12 md:w-12 shrink-0 rounded border bg-background flex items-center justify-center overflow-hidden">
                {showImg ? (
                  <img
                    src={l.image as string}
                    alt=""
                    width={64}
                    height={64}
                    loading={eager ? "eager" : "lazy"}
                    decoding="async"
                    fetchPriority={eager ? "high" : ("low" as any)}
                    className="h-full w-full object-cover"
                    onError={() => {
                      setFailedImageIds((prev) => {
                        if (prev.has(l.id)) return prev;
                        const next = new Set(prev);
                        next.add(l.id);
                        return next;
                      });
                    }}
                  />
                ) : l.image ? (
                  <ImageOff className="h-6 w-6 text-muted-foreground/50" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground/50" />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium leading-snug line-clamp-2 break-words">{l.title}</div>
                {l.variant && (
                  <div className="text-xs text-muted-foreground truncate hidden md:block">{l.variant}</div>
                )}
                <div className="text-xs text-muted-foreground md:hidden mt-0.5">{l.qty} × {l.unit}</div>
              </div>
              <div className="hidden md:block text-sm text-muted-foreground truncate">{l.unit}</div>
              <div className="hidden md:block text-sm font-semibold text-right tabular-nums">{l.qty}×</div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OrderLinesExpansion;
